"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssignmentUtils = void 0;
const lodash_1 = require("lodash");
const common_1 = require("../common");
const SeedShuffle_1 = require("../common/SeedShuffle");
const entries_1 = require("../entries");
const AssignmentSettings_1 = require("./AssignmentSettings");
const auth_1 = require("../auth");
const domain_utils_1 = require("@lessonup/domain-utils");
const pins_shared_1 = require("@lessonup/pins-shared");
class AssignmentUtils {
    static filterGameMode(items, type) {
        return (0, lodash_1.filter)(items, (item) => AssignmentUtils.isMode(item, type));
    }
    static isMode(assignment, type) {
        return (assignment && assignment.settings && assignment.settings.type === type) || false;
    }
    static pin(assignment, id) {
        return (assignment && id && assignment.pins[id]) || undefined;
    }
    static pinDictForIds(assignment, ids) {
        return (assignment && ids && (0, lodash_1.pick)(assignment.pins, ids)) || undefined;
    }
    static pins(assignment) {
        if (!assignment || !assignment.pins)
            return [];
        return Object.values(assignment.pins).sort(pins_shared_1.orderableCompareFn);
    }
    static interactivePins(assignment) {
        if (!assignment || !assignment.pins)
            return [];
        return Object.values(assignment.pins).filter((p) => common_1.PinUtils.isInteractive(p));
    }
    static pinCount(assignment) {
        if (!assignment)
            return undefined;
        return (0, lodash_1.size)(assignment.pins);
    }
    static unorderedPins(assignment) {
        if (!assignment || !assignment.pins)
            return [];
        return Object.values(assignment.pins);
    }
    static firstPin(assignment) {
        return (0, lodash_1.first)(this.pins(assignment));
    }
    static isVisibilityState(state) {
        return ['all', 'studentOnly'].includes(state);
    }
    static isVisibleToAll(assignment) {
        return assignment.visibilityState === 'all';
    }
    static isVisibleToStudentsOnly(assignment) {
        return assignment.visibilityState === 'studentOnly';
    }
    static isUserOwner(assignment, userId) {
        return assignment.ownerId === userId;
    }
    // TODO: this should use our auth context instead
    static isUserAllowedToModify(assignment, userId) {
        return auth_1.Auth.isAccessibleForUser(assignment, userId); // TODO Add group member auth logic, see "getAssignmentIfAllowed"
    }
    /** throws an error when the assignment isn't joinable */
    static validateJoinable(settings, requiredType) {
        if (!requiredType.find((t) => t == settings.type)) {
            throw new domain_utils_1.ForbiddenError('assignment is not of correct type');
        }
        if (settings.status == 'closed') {
            throw new domain_utils_1.ForbiddenError('assignment is closed');
        }
        if (settings.type === 'realtime' && !settings.playerSettings.allowNewPlayers) {
            throw new domain_utils_1.ForbiddenError('assignment does not allow new students to join');
        }
    }
    // return all pins that have testPoints
    static pinsWithTestPoints(assignment) {
        return AssignmentUtils.unorderedPins(assignment).filter((pin) => (0, lodash_1.gt)(pin.settings.testPoints, 0));
    }
    static totalTestPoints(assignment) {
        return (0, lodash_1.sumBy)(AssignmentUtils.unorderedPins(assignment), (pin) => pin.settings.testPoints || 0);
    }
    static getPinShuffleList(pins, userId) {
        let pinOrder = [];
        let pinsToShuffle = [];
        pins.forEach((pin) => {
            if (common_1.PinUtils.isInteractive(pin) && !pin.parentPinId) {
                pinsToShuffle.push(pin._id);
            }
            else {
                if (pinsToShuffle.length) {
                    pinOrder = pinOrder.concat(SeedShuffle_1.SeedShuffle.shuffle(pinsToShuffle, userId));
                    pinsToShuffle = [];
                }
                pinOrder.push(pin._id);
            }
        });
        if (pinsToShuffle.length) {
            pinOrder = pinOrder.concat(SeedShuffle_1.SeedShuffle.shuffle(pinsToShuffle, userId));
        }
        return pinOrder;
    }
    static pinsExcluding(pins, exclusion) {
        return pins.filter((pin) => !pin.exclusions || !(0, lodash_1.includes)(pin.exclusions, exclusion));
    }
    /** returns all student pins without exclusions based on the assignment type */
    static pinsByAssignmentType(assignment) {
        const exclusion = assignment.type === 'realtime' ? 'realtime' : 'homework';
        return this.pinsExcluding(AssignmentUtils.pins(assignment), exclusion);
    }
    static pinIsInAssignment(pin, assignment) {
        const type = assignment.type === 'realtime' ? 'realtime' : 'homework';
        return !(0, lodash_1.includes)(pin.exclusions, type);
    }
    static mapPinsToShuffleMap(pins, userId) {
        const pinOrder = this.getPinShuffleList(pins, userId);
        const newPins = [];
        if (pinOrder.length) {
            pinOrder.forEach((pinId) => {
                const pin = pins.find((p) => p._id == pinId);
                if (!pin)
                    return;
                newPins.push(pin);
            });
        }
        return newPins;
    }
    static pinsOrShuffledPinsForAssignment(assignment, pins, userId) {
        return assignment.settings.type === 'test' && assignment.settings.randomiseQuestions
            ? this.mapPinsToShuffleMap(pins, userId)
            : pins;
    }
    // TODO: type this
    static getShuffledQuizAnswers(shuffledIndices, quizAnswers) {
        if (shuffledIndices.length != quizAnswers.length) {
            throw new domain_utils_1.InvalidParamsError('Arrays are not in sync');
        }
        return shuffledIndices.map((index) => quizAnswers[index]);
    }
    static disallowForEntryIfLinear(assignment, entry) {
        return AssignmentSettings_1.AssignmentSettings.assignmentIsLinear(assignment.settings) && !entries_1.AssignmentEntry.isTestPhase(entry, 'review');
    }
    static isV2Assignment(assignment) {
        return assignment.version === 'v2';
    }
}
exports.AssignmentUtils = AssignmentUtils;
//# sourceMappingURL=AssignmentUtils.js.map